/**
 * Finger marks in the clay: an equirect height map in the ball's own uv (the same layout as the crack field), its
 * size set per ball (Pace.imp: 1536×768, 3072×1536 on the big ball: about one texel per device pixel on a 3× phone).
 * A press leaves a flat-bottomed cup the size of the fingertip, a narrow lip of clay pushed up around it and faint
 * fingerprint ridges in the bottom; a dragging finger leaves a smeared streak. The marks only form where the shell
 * is broken and the clay is soft: every texel is weighted by the softness of the plate it belongs to.
 *
 * Stored as RGBA8 for the shader: the height as 16 bits, R the high byte and B the low byte (32768 = flat,
 * ±IMPRINT_RANGE of the radius), G how freshly pressed (smoothed, slightly shinier) the clay looks. The vertex stage
 * reads only R (filtered, from a coarse mip); the fragment stage all 16 bits — at one texel per device pixel, 8-bit
 * steps show in the shading as contour lines round every cup.
 */
/** the height spans ±IMPRINT_RANGE (fraction of the ball radius) */
export const IMPRINT_RANGE = 0.06;

export type StampOpts = {
  /** cup depth (fraction of R, before softness) */
  depth: number;
  /** the lip of clay pushed up around the cup, as a fraction of `depth` */
  rim?: number;
  /** fingerprint ridges in the bottom of the cup, as a fraction of `depth` (0 = none, e.g. for a smear) */
  ridges?: number;
  /** stretch the print along the unit tangent `along` (a dragging finger), 1 = round */
  stretch?: number;
  along?: ArrayLike<number>;
};

// per-row and per-column trig of the texel centres (row 0 = south pole, as the field), one set per map size
type Trig = { rowSin: Float64Array; rowCos: Float64Array; colCos: Float64Array; colSin: Float64Array };
const trigs = new Map<number, Trig>();
function trigFor(W: number, H: number): Trig {
  const key = W * 65536 + H;
  let t = trigs.get(key);
  if (!t) {
    t = { rowSin: new Float64Array(H), rowCos: new Float64Array(H), colCos: new Float64Array(W), colSin: new Float64Array(W) };
    for (let y = 0; y < H; y++) { const th = (1 - (y + 0.5) / H) * Math.PI; t.rowSin[y] = Math.sin(th); t.rowCos[y] = Math.cos(th); }
    for (let x = 0; x < W; x++) { const ph = ((x + 0.5) / W) * Math.PI * 2; t.colCos[x] = Math.cos(ph); t.colSin[x] = Math.sin(ph); }
    trigs.set(key, t);
  }
  return t;
}

export class ImprintMap {
  readonly W: number;
  readonly H: number;
  /** height per texel, fraction of R */
  private h: Float32Array;
  /** RG8 bytes for the texture (G, how freshly pressed, lives only here) */
  data: Uint8Array;
  dirty = false;
  /** rows written since the last upload, [dirtyY0, dirtyY1) */
  dirtyY0 = 0;
  dirtyY1 = 0;
  private seed: number;
  private trig: Trig;

  constructor(seed: number, W = 768, H = 384) {
    this.seed = seed; this.W = W; this.H = H;
    this.h = new Float32Array(W * H);
    this.data = new Uint8Array(W * H * 4);
    for (let i = 0; i < W * H; i++) this.data[i * 4] = 128;
    this.trig = trigFor(W, H);
  }

  /** The print's height (fraction of R) at unit direction `a` (nearest texel). */
  heightAt(a: ArrayLike<number>) {
    const W = this.W, H = this.H;
    let phi = Math.atan2(a[2], -a[0]); if (phi < 0) phi += Math.PI * 2;
    const th = Math.acos(Math.max(-1, Math.min(1, a[1])));
    const x = Math.min(W - 1, Math.floor((phi / (Math.PI * 2)) * W)), y = Math.min(H - 1, Math.max(0, Math.floor((1 - th / Math.PI) * H)));
    return this.h[y * W + x];
  }

  /**
   * Presses a fingertip print centred on unit direction `c` (ball frame) with radius `radius` (rad).
   * Softness per texel = `plateSoft[id]` of the plate owning the field texel there (`ids`: the field's RG ids, a
   * fW×fH map).
   */
  stamp(c: ArrayLike<number>, radius: number, ids: Uint8Array, fW: number, fH: number, plateSoft: Float32Array, o: StampOpts) {
    const IMP_W = this.W, IMP_H = this.H, { rowSin: ROW_SIN, rowCos: ROW_COS, colCos: COL_COS, colSin: COL_SIN } = this.trig;
    const fxK = fW / IMP_W, fyK = fH / IMP_H; // imprint texel → field texel
    const rimK = o.rim ?? 0.5, ridgeK = o.ridges ?? 0.14;
    const st = Math.max(1, o.stretch ?? 1), al = st > 1 ? o.along : undefined;
    // the print's angular reach (stretched along a drag), kept well inside a hemisphere
    const reach = Math.min(Math.PI / 2 - 0.05, radius * 1.3 * st), cosR = Math.cos(reach);
    // tangent frame at c (for the ridges' whorl and a drag's stretch)
    const ux = Math.abs(c[1]) < 0.9 ? 0 : 1, uy = 1 - ux, k = ux * c[0] + uy * c[1];
    let t1x = ux - c[0] * k, t1y = uy - c[1] * k, t1z = -c[2] * k;
    const tl = Math.hypot(t1x, t1y, t1z) || 1; t1x /= tl; t1y /= tl; t1z /= tl;
    const t2x = c[1] * t1z - c[2] * t1y, t2y = c[2] * t1x - c[0] * t1z, t2z = c[0] * t1y - c[1] * t1x;
    let a1 = 0, a2 = 0;
    if (al) {
      a1 = al[0] * t1x + al[1] * t1y + al[2] * t1z; a2 = al[0] * t2x + al[1] * t2y + al[2] * t2z;
      const l = Math.hypot(a1, a2) || 1; a1 /= l; a2 /= l;
    }
    // fingerprint: concentric ridges about 6 texels apart (~6 device px on a 3× phone), a little elliptic and
    // off-centre, so it reads as a whorl. Only a trace of them: at the maps' old size (ridges 3 texels apart) the
    // filtering all but erased them, and fully resolved they read as contour stripes over the whole crushed patch
    const spacing = (6.4 * Math.PI) / IMP_H, phase = (this.seed % 997) * 0.37, ridgeAmp = 0.3;
    // rows the cap touches (row 0 = south pole), and per row the cap's exact half-width in longitude
    const thc = Math.acos(Math.max(-1, Math.min(1, c[1]))), sinC = Math.sin(thc), cosC = Math.cos(thc);
    const y0 = Math.max(0, Math.floor(IMP_H * (1 - (thc + reach) / Math.PI)) - 1);
    const y1 = Math.min(IMP_H - 1, Math.ceil(IMP_H * (1 - (thc - reach) / Math.PI)) + 1);
    let phc = Math.atan2(c[2], -c[0]); if (phc < 0) phc += Math.PI * 2;
    const cx = Math.floor((phc / (Math.PI * 2)) * IMP_W);
    let wy0 = IMP_H, wy1 = -1;
    for (let y = y0; y <= y1; y++) {
      const sinT = ROW_SIN[y], cosT = ROW_COS[y];
      const den = sinT * sinC;
      let cols: number;
      if (den < 1e-9) cols = IMP_W / 2;
      else {
        const cd = (cosR - cosT * cosC) / den;
        if (cd >= 1) continue; // this row lies outside the cap
        cols = cd <= -1 ? IMP_W / 2 : Math.ceil((Math.acos(cd) / (Math.PI * 2)) * IMP_W) + 1;
        if (cols > IMP_W / 2) cols = IMP_W / 2;
      }
      const fy = Math.min(fH - 1, ((y + 0.5) * fyK) | 0);
      for (let kx = -cols; kx < cols; kx++) {
        const x = (cx + kx + IMP_W * 4) % IMP_W;
        const dx = -COL_COS[x] * sinT, dy = cosT, dz = COL_SIN[x] * sinT;
        const dp = dx * c[0] + dy * c[1] + dz * c[2];
        if (dp < cosR) continue;
        // position in the tangent plane as true angles, a drag's stretch undone along its direction
        let u = dx * t1x + dy * t1y + dz * t1z, v = dx * t2x + dy * t2y + dz * t2z;
        const lt = Math.sqrt(u * u + v * v);
        if (lt > 1e-9) { const g = Math.atan2(lt, dp) / lt; u *= g; v *= g; }
        if (al) { const pa = u * a1 + v * a2, sh = pa * (1 - 1 / st); u -= a1 * sh; v -= a2 * sh; }
        const q = Math.sqrt(u * u + v * v) / radius;
        if (q > 1.3) continue;
        const soft = plateSoft[ids[(fy * fW + Math.min(fW - 1, ((x + 0.5) * fxK) | 0)) * 2]] ?? 0;
        if (soft <= 0.01) continue;
        // a flat-bottomed cup with a steep wall, a narrow lip just outside, ridges on the floor
        let dh = 0;
        if (q < 1) { const t = Math.max(0, (q - 0.55) / 0.45), w = 1 - t * t * (3 - 2 * t); dh -= o.depth * w; }
        dh += o.depth * rimK * Math.exp(-((q - 1.06) * (q - 1.06)) / (2 * 0.16 * 0.16));
        if (ridgeK > 0 && q < 0.85) {
          const e = Math.sqrt((u * 1.15 + 0.12 * radius) ** 2 + (v * 0.9) ** 2);
          dh += o.depth * ridgeK * ridgeAmp * Math.cos((2 * Math.PI * e) / spacing + phase) * (1 - q / 0.85);
        }
        const i = y * IMP_W + x;
        const hv = Math.max(-IMPRINT_RANGE, Math.min(IMPRINT_RANGE, this.h[i] + dh * soft));
        const wv = Math.max(this.data[i * 4 + 1], Math.round(255 * soft * Math.max(0, 1 - q / 1.15)));
        if (hv === this.h[i] && wv === this.data[i * 4 + 1]) continue;
        this.h[i] = hv;
        const hq = Math.max(0, Math.min(65535, Math.round(32768 + (hv / IMPRINT_RANGE) * 32767)));
        this.data[i * 4] = hq >> 8; this.data[i * 4 + 2] = hq & 255;
        this.data[i * 4 + 1] = wv;
        if (y < wy0) wy0 = y;
        wy1 = y;
      }
    }
    if (wy1 >= 0) {
      if (!this.dirty) { this.dirtyY0 = wy0; this.dirtyY1 = wy1 + 1; }
      else { this.dirtyY0 = Math.min(this.dirtyY0, wy0); this.dirtyY1 = Math.max(this.dirtyY1, wy1 + 1); }
      this.dirty = true;
    }
  }
}
